import {
  convertFileToBase64,
  removeTelephoneCountryCode,
  STATUSPAGE,
} from '@repo/ui';
import {
  CreateEntitiesUseCaseInterface,
  DeleteEntitiesUseCaseInterface,
  UpdateEntitiesUseCaseInterface,
  GetByIdEntitiesUseCaseInterface,
  GetAddressViaZipCodeUseCaseInterface,
  GetAllCitiesForSelectOptionsUseCaseInterface,
  GetAllInsuranceCompaniesUseCaseInterface,
  GetAllSsoProvidersUseCaseInterface,
  GetSearchByCnpjUseCaseInterface,
  UploadPhotoUseCaseInterface,
  DeletePhotoUseCaseInterface,
  DeleteBannerUseCaseInterface,
  UploadBannerUseCaseInterface,
  DeleteFieldAssessmentsUseCaseInterface,
  UpdateFieldAssessmentsUseCaseInterface,
  CreateFieldAssessmentsUseCaseInterface,
  GetAllFieldAssessmentsUseCaseInterface,
  GetByIdFieldAssessmentsUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadEntitiesListData } from 'presentation/config/store/entities/entities-slice';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import {
  addressSchema,
  cnpjSchema,
  imageFileSchema,
  labelValueSchema,
  optionalStringSchema,
  representativeSchema,
  requiredStringSchema,
  schoolPeriodSchema,
  settingSchema,
  supportSchema,
  tceSchema,
} from '../../../shared/schemas';
import { normalizePeriod } from '../common/helpers/normalize-period';
import {
  initialValuesAddress,
  initialValuesEntitiesFormatPhone,
  initialValuesEntitiesGetApiIntegrationCode,
  initialValuesEntitiesGetCnpj,
  initialValuesEntitiesGetCorporateName,
  initialValuesEntitiesGetEmail,
  initialValuesEntitiesGetPhoto,
  initialValuesEntitiesGetSite,
  initialValuesEntitiesGetTradeName,
  initialValuesRepresentative,
  initialValuesSchoolPeriod,
  initialValuesSetting,
  initialValuesSupport,
  initialValuesTce,
  initialValuesType,
} from '../validation-form/initial-values';

const mapSsoSettingToPayload = (setting: any) => ({
  enabled: !!setting?.sso?.enabled,
  providers: setting?.sso?.enabled
    ? (setting?.sso?.providers || []).map((provider: any) => ({
        ssoProviderId: provider.ssoProviderId?.value,
        domain: provider.domain,
        clientId: provider.clientId,
        tenantId: provider.tenantId,
        secretKey: provider.secretKey,
      }))
    : undefined,
});

export interface LoadingStates {
  uploadPhoto: STATUSPAGE;
  deletePhoto: STATUSPAGE;
  deleteBanner: STATUSPAGE;
  uploadBanner: STATUSPAGE;
  getByIdEntities: STATUSPAGE;
  deleteFieldAssessments: STATUSPAGE;
  updateFieldAssessments: STATUSPAGE;
  createFieldAssessments: STATUSPAGE;
  getAllFieldAssessments: STATUSPAGE;
  getByIdFieldAssessments: STATUSPAGE;
}

export const useEntities = () => {
  const { entityId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasChangedAddressFields = useRef(false);

  const {
    entitiesService,
    locationsService,
    insuranceCompaniesService,
    searchByCnpjService,
    partnersService,
    ssoProvidersService,
  } = useService();
  // loading states
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    uploadPhoto: STATUSPAGE.DONE,
    deletePhoto: STATUSPAGE.DONE,
    deleteBanner: STATUSPAGE.DONE,
    uploadBanner: STATUSPAGE.DONE,
    getByIdEntities: STATUSPAGE.DONE,
    deleteFieldAssessments: STATUSPAGE.DONE,
    updateFieldAssessments: STATUSPAGE.DONE,
    createFieldAssessments: STATUSPAGE.DONE,
    getAllFieldAssessments: STATUSPAGE.DONE,
    getByIdFieldAssessments: STATUSPAGE.DONE,
  });

  const [isModalFieldAssessmentsOpen, setIsModalFieldAssessmentsOpen] =
    useState(false);
  const toggleModalFieldAssessments = () => {
    setIsModalFieldAssessmentsOpen(!isModalFieldAssessmentsOpen);
  };
  const [deleteTce, setDeleteTce] = useState(false);

  // States
  const { entitiesListData } = useAppSelector((state) => state.Entities);
  const [listStatus, setListStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [statusCreateEntities, setStatusCreateEntities] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [statusEditEntities, setStatusEditEntities] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [statusDeleteEntities, setStatusDeleteEntities] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );

  const [searchParams] = useSearchParams();

  const activeTabId = searchParams.get('painel');

  const [
    internshipLocationPhotoImagePreviewSrc,
    setInternshipLocationPhotoImagePreviewSrc,
  ] = useState('');

  const handleDeleteBanner = () => {
    requestDeleteBanner({ id: entityId });
  };

  const handleDeletePhoto = () => {
    requestDeletePhoto({ id: entityId });
  };

  const [isModalBannerOpen, setIsModalBannerOpen] = useState(false);
  const handleToggleModal = () => {
    setIsModalBannerOpen(!isModalBannerOpen);
  };

  const [isModalPhotoOpen, setIsModalPhotoOpen] = useState(false);
  const handleToggleModalPhoto = () => {
    setIsModalPhotoOpen(!isModalPhotoOpen);
  };

  const [listFieldAssessments, setListFieldAssessments] =
    useState<GetAllFieldAssessmentsUseCaseInterface.Result>([]);

  const [getByIdResponseFieldAssessments, setGetByIdResponseFieldAssessments] =
    useState<GetByIdFieldAssessmentsUseCaseInterface.Result | null>(null);

  const [responseEntitiesById, setResponseEntitiesById] =
    useState<GetByIdEntitiesUseCaseInterface.Result>();

  const [
    getAddressViaZipCodeRequestStatus,
    setGetAddressViaZipCodeRequestStatus,
  ] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [loadingEntitiesSetting, setLoadingEntitiesSetting] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [listInsuranceCompanies, setInsuranceCompanies] =
    useState<GetAllInsuranceCompaniesUseCaseInterface.Result>();

  const [listSsoProviders, setListSsoProviders] =
    useState<GetAllSsoProvidersUseCaseInterface.Result>();

  const [
    getAllCitiesForSelectOptionsRequestStatus,
    setGetAllCitiesForSelectOptionsRequestStatus,
  ] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  //
  const [searchByCnpjData, setSearchByCnpjData] =
    useState<GetSearchByCnpjUseCaseInterface.Result>();
  const [loadingSearchByCnpj, setLoadingSearchByCnpj] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );

  const autoFillAddressInputValuesAccordinglyToZipCodeAPI = useCallback(
    ({
      logradouro,
      bairro,
      ibge,
      localidade,
    }: GetAddressViaZipCodeUseCaseInterface.Result) => {
      currentValidation.setFieldValue('address.street', logradouro || '');
      currentValidation.setFieldValue('address.neighborhood', bairro || '');
      currentValidation.setFieldValue(
        'address.city',
        localidade && ibge
          ? {
              label: localidade,
              value: Number(ibge),
            }
          : null,
      );
    },
    [],
  );

  const clearAddressInputValues = useCallback(() => {
    currentValidation.setFieldValue('address.street', '');
    currentValidation.setFieldValue('address.district', '');
    currentValidation.setFieldValue('address.city', null);
  }, []);

  // set address input values
  const autoFillAddressInputValuesAccordinglyToCnpjAPI = useCallback(
    ({
      cityId,
      companyName,
      neighborhood,
      street,
      tradeName,
      zipCode,
      cityName,
    }: GetSearchByCnpjUseCaseInterface.Result) => {
      currentValidation.setFieldValue('address.street', street || '');
      currentValidation.setFieldValue(
        'address.neighborhood',
        neighborhood || '',
      );
      currentValidation.setFieldValue(
        'address.city',
        cityName && cityId
          ? {
              label: cityName,
              value: Number(cityId),
            }
          : null,
      );
      currentValidation.setFieldValue('address.zipCode', zipCode || '');
      currentValidation.setFieldValue('corporateName', companyName || '');
      currentValidation.setFieldValue('tradeName', tradeName || '');
    },
    [],
  );

  // Update do form de dados cadastrais de entidade
  const updateEntitiesRegistrationData = (
    data: Pick<
      UpdateEntitiesUseCaseInterface.Params,
      | 'type'
      | 'cnpj'
      | 'apiIntegrationCode'
      | 'corporateName'
      | 'tradeName'
      | 'representative'
      | 'contacts'
      | 'support'
      | 'address'
      | 'schoolPeriods'
    >,
  ) => {
    requestEntitiesUpdate({
      ...data,
      id: entityId,
    });
  };

  // Update do form de configuração de entidade
  const updateEntitiesSettings = (
    data: Pick<UpdateEntitiesUseCaseInterface.Params, 'settings' | 'tce'>,
  ) => {
    requestEntitiesSettingUpdate({
      ...data,
      id: entityId,
    });
  };

  const [responseEntitiesSettingById, setResponseEntitiesSettingById] =
    useState<GetByIdEntitiesUseCaseInterface.Result | null>(null);

  const schoolPeriodInitialValues = useMemo(
    () => initialValuesSchoolPeriod(responseEntitiesById),
    [
      responseEntitiesById?.schoolPeriods?.firstPeriod?.startDate,
      responseEntitiesById?.schoolPeriods?.firstPeriod?.endDate,
      responseEntitiesById?.schoolPeriods?.secondPeriod?.startDate,
      responseEntitiesById?.schoolPeriods?.secondPeriod?.endDate,
    ],
  );

  const validationInitialValues = useMemo(
    () => ({
      type: initialValuesType(responseEntitiesById),
      support: initialValuesSupport(responseEntitiesById),
      address: initialValuesAddress(responseEntitiesById),
      cnpj: initialValuesEntitiesGetCnpj(responseEntitiesById),
      site: initialValuesEntitiesGetSite(responseEntitiesById),
      email: initialValuesEntitiesGetEmail(responseEntitiesById),
      photo: initialValuesEntitiesGetPhoto(responseEntitiesById),
      representative: initialValuesRepresentative(responseEntitiesById),
      tradeName: initialValuesEntitiesGetTradeName(responseEntitiesById),
      phone: initialValuesEntitiesFormatPhone(responseEntitiesById?.phone),
      corporateName:
        initialValuesEntitiesGetCorporateName(responseEntitiesById),
      apiIntegrationCode:
        initialValuesEntitiesGetApiIntegrationCode(responseEntitiesById),
      tce: initialValuesTce(responseEntitiesSettingById),
      setting: initialValuesSetting(responseEntitiesSettingById),
      schoolPeriods: schoolPeriodInitialValues.schoolPeriods,
    }),
    [
      responseEntitiesById,
      responseEntitiesSettingById,
      schoolPeriodInitialValues,
    ],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: validationInitialValues,
    validationSchema: Yup.object().shape({
      tce: tceSchema,
      cnpj: cnpjSchema,
      photo: imageFileSchema,
      support: supportSchema,
      address: addressSchema,
      site: optionalStringSchema,
      phone: optionalStringSchema,
      email: optionalStringSchema,
      tradeName: requiredStringSchema,
      corporateName: requiredStringSchema,
      representative: representativeSchema,
      apiIntegrationCode: optionalStringSchema,
      type: labelValueSchema.required('Campo obrigatório'),
      schoolPeriods: schoolPeriodSchema,
      setting: settingSchema,
    }),
    onSubmit: async (values) => {
      const {
        tce: { rescissionTermModel },
      } = values;

      let correctTypeFile = rescissionTermModel as
        | string
        | File
        | null
        | undefined;
      let base64File: string;

      if (correctTypeFile && correctTypeFile instanceof File) {
        base64File = await convertFileToBase64(correctTypeFile);
      }

      const firstPeriod = normalizePeriod(values.schoolPeriods.firstPeriod);
      const secondPeriod = normalizePeriod(values.schoolPeriods.secondPeriod);
      requestEntitiesCreate({
        address: {
          street: values.address.street,
          number: values.address.number,
          neighborhood: values.address.neighborhood,
          complement: values.address.complement,
          cityId: values?.address?.city?.value,
          zipCode: values.address.zipCode,
        },
        type: Number(values.type.value),
        cnpj: values.cnpj,
        apiIntegrationCode: values.apiIntegrationCode,
        corporateName: values.corporateName,
        tradeName: values.tradeName,
        representative: {
          name: values.representative.name,
          role: values.representative.role,
        },
        contacts: {
          phone: removeTelephoneCountryCode(values.phone),
          email: values.email,
          site: values.site,
        },

        support: {
          phoneNumber: removeTelephoneCountryCode(values.support.phoneNumber),
          contactName: values.support.contactName,
        },

        tce: {
          insuranceCompanyId:
            Number(values?.tce?.insuranceCompanyId?.value) || null,
          policy: values.tce.policy,
          rescissionTermModel: base64File || null,
        },

        schoolPeriods: {
          firstPeriod,
          secondPeriod,
        },
        settings: {
          appStudentBlockActivityScheduling:
            values.setting.appStudentBlockActivityScheduling,
          appStudentBlockQrCodeActivityStart:
            values.setting.appStudentBlockQrCodeActivityStart,
          toleratedDistanceFromInternshipLocation:
            values.setting.toleratedDistanceFromInternshipLocation,
          toleranceStartMinutes: Number(values.setting.toleranceStartMinutes),
          toleranceEndMinutes: Number(values.setting.toleranceEndMinutes),
          appStudentHideTotalWorkload:
            values.setting.appStudentHideTotalWorkload,
          showFinalActivityEvaluation:
            values.setting.showFinalActivityEvaluation,
          finalActivityEvaluationRequired:
            values.setting.finalActivityEvaluationRequired,
          notifyWppTeacherPendingActivity:
            values.setting.notifyWppTeacherPendingActivity,
          appStudentStartOnlyToleratedDistance:
            values.setting.appStudentStartOnlyToleratedDistance,
          appStudentFinishOnlyToleratedDistance:
            values.setting.appStudentFinishOnlyToleratedDistance,
          appProfessionalShowFeedbackValidation:
            values.setting.appProfessionalShowFeedbackValidation,
          appProfessionalShowDailyWorkloadByStudentReport:
            values.setting.appProfessionalShowDailyWorkloadByStudentReport,
          appProfessionalSubtaskRequiredByDefault:
            values.setting.appProfessionalSubtaskRequiredByDefault,
          appStudentHideWorkloadValidated:
            values.setting.appStudentHideWorkloadValidated,
          appStudentEvaluationField: values.setting.appStudentEvaluationField,
          appStudentAllowGenerateQrcodeEvaluation:
            values.setting.appStudentAllowGenerateQrcodeEvaluation,
          appStudentDemandStudentPhotoOnActivityStart:
            values.setting.appStudentDemandStudentPhotoOnActivityStart,
          appStudentDemandStudentPhotoOnActivityEnd:
            values.setting.appStudentDemandStudentPhotoOnActivityEnd,
          timeLimitInDaysToJustifyActivities:
            values.setting.timeLimitInDaysToJustifyActivities,
          appStudentConsiderAutoFinishedActivityAsMissed:
            values.setting.appStudentConsiderAutoFinishedActivityAsMissed,
          appStudentConsiderActivityWithLocationBlockAsMissed:
            values.setting.appStudentConsiderActivityWithLocationBlockAsMissed,
          appStudentCanEditInstitutionScheduledActivity:
            values.setting.appStudentCanEditInstitutionScheduledActivity,
          allowOrganizationQrCodeEvaluationAtActivityEnd:
            values.setting.allowOrganizationQrCodeEvaluationAtActivityEnd,
          appProfessionalShowFaultInValidation:
            values.setting.appProfessionalShowFaultInValidation,
          partners: values.setting.partners,
          defaultEvaluationDisplayable:
            values.setting.defaultEvaluationDisplayable,
          appStudentAllowReschedulingMissedActivities:
            values.setting.appStudentAllowReschedulingMissedActivities,
          allowExternalProfessionalActivityValidation:
            values.setting.allowExternalProfessionalActivityValidation,
          appStudentDemandFileOnActivityJustification:
            values.setting.appStudentDemandFileOnActivityJustification,
          appStudentAllowEvaluationAcceptanceByStudent:
            values.setting.appStudentAllowEvaluationAcceptanceByStudent,
          sso: mapSsoSettingToPayload(values.setting),
        },
      });
    },
  });

  const validationFormsSetting = useFormik({
    enableReinitialize: true,
    initialValues: {
      tce: initialValuesTce(responseEntitiesSettingById),
      setting: initialValuesSetting(responseEntitiesSettingById),
    },
    validationSchema: Yup.object().shape({
      tce: tceSchema,
      setting: settingSchema,
    }),
    onSubmit: async (values) => {
      const {
        tce: { rescissionTermModel },
      } = values;

      let correctTypeFile = rescissionTermModel as
        | string
        | File
        | null
        | undefined;
      let base64File: string;

      if (correctTypeFile && correctTypeFile instanceof File) {
        base64File = await convertFileToBase64(correctTypeFile);
      }
      updateEntitiesSettings({
        settings: {
          appStudentBlockActivityScheduling:
            values.setting.appStudentBlockActivityScheduling,
          appStudentBlockQrCodeActivityStart:
            values.setting.appStudentBlockQrCodeActivityStart,
          toleratedDistanceFromInternshipLocation:
            values.setting.toleratedDistanceFromInternshipLocation,
          toleranceStartMinutes: Number(values.setting.toleranceStartMinutes),
          toleranceEndMinutes: Number(values.setting.toleranceEndMinutes),
          timeLimitInDaysToJustifyActivities:
            values.setting.timeLimitInDaysToJustifyActivities || null,
          appStudentHideTotalWorkload:
            values.setting.appStudentHideTotalWorkload,
          showFinalActivityEvaluation:
            values.setting.showFinalActivityEvaluation,
          finalActivityEvaluationRequired:
            values.setting.finalActivityEvaluationRequired,
          notifyWppTeacherPendingActivity:
            values.setting.notifyWppTeacherPendingActivity,
          appStudentStartOnlyToleratedDistance:
            values.setting.appStudentStartOnlyToleratedDistance,
          appStudentFinishOnlyToleratedDistance:
            values.setting.appStudentFinishOnlyToleratedDistance,
          appProfessionalShowFeedbackValidation:
            values.setting.appProfessionalShowFeedbackValidation,
          appProfessionalShowDailyWorkloadByStudentReport:
            values.setting.appProfessionalShowDailyWorkloadByStudentReport,
          appProfessionalSubtaskRequiredByDefault:
            values.setting.appProfessionalSubtaskRequiredByDefault,
          appStudentHideWorkloadValidated:
            values.setting.appStudentHideWorkloadValidated,
          appStudentEvaluationField: values.setting.appStudentEvaluationField,
          appStudentAllowGenerateQrcodeEvaluation:
            values.setting.appStudentAllowGenerateQrcodeEvaluation,
          appStudentDemandStudentPhotoOnActivityStart:
            values.setting.appStudentDemandStudentPhotoOnActivityStart,
          appStudentDemandStudentPhotoOnActivityEnd:
            values.setting.appStudentDemandStudentPhotoOnActivityEnd,
          appStudentConsiderAutoFinishedActivityAsMissed:
            values.setting.appStudentConsiderAutoFinishedActivityAsMissed,
          appStudentConsiderActivityWithLocationBlockAsMissed:
            values.setting.appStudentConsiderActivityWithLocationBlockAsMissed,
          appStudentCanEditInstitutionScheduledActivity:
            values.setting.appStudentCanEditInstitutionScheduledActivity,
          allowOrganizationQrCodeEvaluationAtActivityEnd:
            values.setting.allowOrganizationQrCodeEvaluationAtActivityEnd,
          appProfessionalShowFaultInValidation:
            values.setting.appProfessionalShowFaultInValidation,
          partners: values.setting.partners,
          defaultEvaluationDisplayable:
            values.setting.defaultEvaluationDisplayable,
          appStudentAllowReschedulingMissedActivities:
            values.setting.appStudentAllowReschedulingMissedActivities,
          allowExternalProfessionalActivityValidation:
            values.setting.allowExternalProfessionalActivityValidation,
          appStudentDemandFileOnActivityJustification:
            values.setting.appStudentDemandFileOnActivityJustification,
          appStudentAllowEvaluationAcceptanceByStudent:
            values.setting.appStudentAllowEvaluationAcceptanceByStudent,
          sso: mapSsoSettingToPayload(values.setting),
        },
        tce: {
          deleteTce: deleteTce,
          insuranceCompanyId:
            Number(values?.tce?.insuranceCompanyId?.value) || null,
          policy: values.tce.policy,
          rescissionTermModel:
            typeof validationFormsSetting.values.tce.rescissionTermModel ===
            'string'
              ? undefined
              : base64File === undefined
                ? validationFormsSetting.values.tce.rescissionTermModel
                : base64File === null
                  ? null
                  : base64File,
        },
      });
    },
  });

  const validationFormRegistrationData = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: initialValuesType(responseEntitiesById),
      support: initialValuesSupport(responseEntitiesById),
      address: initialValuesAddress(responseEntitiesById),
      cnpj: initialValuesEntitiesGetCnpj(responseEntitiesById),
      site: initialValuesEntitiesGetSite(responseEntitiesById),
      email: initialValuesEntitiesGetEmail(responseEntitiesById),
      photo: initialValuesEntitiesGetPhoto(responseEntitiesById),
      representative: initialValuesRepresentative(responseEntitiesById),
      tradeName: initialValuesEntitiesGetTradeName(responseEntitiesById),
      phone: initialValuesEntitiesFormatPhone(responseEntitiesById?.phone),
      corporateName:
        initialValuesEntitiesGetCorporateName(responseEntitiesById),
      apiIntegrationCode:
        initialValuesEntitiesGetApiIntegrationCode(responseEntitiesById),
      schoolPeriods: schoolPeriodInitialValues.schoolPeriods,
    },
    validationSchema: Yup.object().shape({
      cnpj: cnpjSchema,
      photo: imageFileSchema,
      support: supportSchema,
      address: addressSchema,
      site: optionalStringSchema,
      phone: optionalStringSchema,
      email: optionalStringSchema,
      tradeName: requiredStringSchema,
      corporateName: requiredStringSchema,
      representative: representativeSchema,
      apiIntegrationCode: optionalStringSchema,
      type: labelValueSchema.required('Campo obrigatório'),
      schoolPeriods: schoolPeriodSchema,
    }),
    onSubmit: async (values) => {
      const firstPeriod = normalizePeriod(values.schoolPeriods.firstPeriod);
      const secondPeriod = normalizePeriod(values.schoolPeriods.secondPeriod);

      updateEntitiesRegistrationData({
        address: {
          street: values.address.street,
          number: values.address.number,
          zipCode: values.address.zipCode,
          cityId: values?.address?.city?.value,
          complement: values.address.complement,
          neighborhood: values.address.neighborhood,
        },
        cnpj: values.cnpj,
        tradeName: values.tradeName,
        corporateName: values.corporateName,
        type: Number(values.type.value),
        apiIntegrationCode: values.apiIntegrationCode,
        representative: {
          name: values.representative.name,
          role: values.representative.role,
        },
        contacts: {
          phone: removeTelephoneCountryCode(values.phone),
          email: values.email,
          site: values.site,
        },
        support: {
          phoneNumber: removeTelephoneCountryCode(values.support.phoneNumber),
          contactName: values.support.contactName,
        },
        schoolPeriods: {
          firstPeriod,
          secondPeriod,
        },
      });
    },
  });

  const currentValidation =
    activeTabId === 'dados-cadastrais'
      ? validationFormRegistrationData
      : validation;

  // list entities
  const requestEntitiesList = useCallback(async () => {
    setListStatus(STATUSPAGE.LOADING);
    return entitiesService
      .getAllEntities()
      .then((res) => {
        dispatch(loadEntitiesListData(res));
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao listar organizações');
      })
      .finally(() => {
        setListStatus(STATUSPAGE.DONE);
      });
  }, []);

  // create entities
  const requestEntitiesCreate = useCallback(
    async (params: CreateEntitiesUseCaseInterface.Params) => {
      setStatusCreateEntities(STATUSPAGE.LOADING);

      return entitiesService
        .createEntities(params)
        .then((res) => {
          notifySuccess(res.message || 'Organização criado com sucesso');
          requestEntitiesList();
          navigate(`/organizacao/${res.id}?painel=dados-cadastrais`);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao criar uma organização');
        })
        .finally(() => {
          setStatusCreateEntities(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // delete entities
  const requestEntitiesDelete = useCallback(
    async (params: DeleteEntitiesUseCaseInterface.Params) => {
      setStatusDeleteEntities(STATUSPAGE.LOADING);
      return entitiesService
        .deleteEntities(params)
        .then((res) => {
          notifySuccess(res.message || 'Organização excluída com sucesso.');
          requestEntitiesList();
          navigate('/organizacoes');
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao excluir uma organização.');
        })
        .finally(() => {
          setStatusDeleteEntities(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // update entities
  const requestEntitiesUpdate = useCallback(
    async (params: UpdateEntitiesUseCaseInterface.Params) => {
      setStatusEditEntities(STATUSPAGE.LOADING);
      return entitiesService
        .updateEntities(params)
        .then((res) => {
          notifySuccess(res.message || 'Organização atualizada com sucesso.');
          requestEntitiesGetById({
            id: params.id,
          });
        })

        .catch((err) => {
          notifyError(err.message || 'Erro ao atualizar uma organizações.');
        })
        .finally(() => {
          setStatusEditEntities(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // get entities by id
  const requestEntitiesGetById = useCallback(
    async (params: GetByIdEntitiesUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        getByIdEntities: STATUSPAGE.LOADING,
      }));
      return entitiesService
        .getByIdEntities(params)
        .then((res: GetByIdEntitiesUseCaseInterface.Result) => {
          setResponseEntitiesById(res);
        })
        .catch((err) => {
          notifyError(
            err.message || 'Um erro inesperado ocorreu, tente novamente.',
          );
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            getByIdEntities: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );
  //

  const requestEntitiesSettingGetById = useCallback(
    async (params: GetByIdEntitiesUseCaseInterface.Params) => {
      return entitiesService
        .getByIdEntities(params)
        .then((res: GetByIdEntitiesUseCaseInterface.Result) => {
          setResponseEntitiesSettingById(res);
        })
        .catch((err) => {
          notifyError(
            err.message || 'Um erro inesperado ocorreu, tente novamente.',
          );
        });
    },
    [],
  );

  const requestEntitiesSettingUpdate = useCallback(
    async (params: UpdateEntitiesUseCaseInterface.Params) => {
      setLoadingEntitiesSetting(STATUSPAGE.LOADING);
      return entitiesService
        .updateEntities(params)
        .then((res) => {
          notifySuccess(res.message || 'Organização atualizada com sucesso.');
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao atualizar uma organizações.');
        })
        .finally(() => {
          setLoadingEntitiesSetting(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const [citiesForSelectOptionsData, setCitiesForSelectOptionsData] = useState(
    [],
  );

  const requestGetAllCitiesForSelectOptions = useCallback(
    async (params: GetAllCitiesForSelectOptionsUseCaseInterface.Params) => {
      setGetAllCitiesForSelectOptionsRequestStatus(STATUSPAGE.LOADING);
      await locationsService
        .getAllCitiesForSelectOptions(params)
        .then((res) => setCitiesForSelectOptionsData(res))
        .catch(() => {
          setCitiesForSelectOptionsData([]);
          console.warn('Não foi possível listar as cidades.');
        })
        .finally(() =>
          setGetAllCitiesForSelectOptionsRequestStatus(STATUSPAGE.DONE),
        );
    },
    [],
  );

  // request address via zip code
  const requestGetAddressViaZipCode = useCallback(
    (params: GetAddressViaZipCodeUseCaseInterface.Params) => {
      setGetAddressViaZipCodeRequestStatus(STATUSPAGE.LOADING);
      locationsService
        .getAddressViaZipCode(params)
        .then((res) => {
          autoFillAddressInputValuesAccordinglyToZipCodeAPI(res);
        })
        .catch((err) => {
          clearAddressInputValues();
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => setGetAddressViaZipCodeRequestStatus(STATUSPAGE.DONE));
    },
    [],
  );

  // list insurance companies
  const requestGetAllInsuranceCompanies = useCallback(async () => {
    return insuranceCompaniesService
      .getAllInsuranceCompanies()
      .then((res) => {
        setInsuranceCompanies(res);
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao listar seguradoras');
      });
  }, []);

  // list sso providers
  const requestGetAllSsoProviders = useCallback(async () => {
    return ssoProvidersService
      .getAllSsoProviders()
      .then((res) => {
        setListSsoProviders(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao listar provedores de SSO');
      });
  }, []);

  // request cnpj
  const requestSearchByCnpj = useCallback(
    (params: GetSearchByCnpjUseCaseInterface.Params) => {
      setLoadingSearchByCnpj(STATUSPAGE.LOADING);
      searchByCnpjService
        .searchCnpj(params)
        .then((res) => {
          setSearchByCnpjData(res);
          autoFillAddressInputValuesAccordinglyToCnpjAPI(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => setLoadingSearchByCnpj(STATUSPAGE.DONE));
    },
    [],
  );

  //Photo
  const requestUploadPhoto = useCallback(
    (params: UploadPhotoUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        uploadPhoto: STATUSPAGE.LOADING,
      }));
      entitiesService
        .uploadPhoto(params)
        .then((res) => {
          notifySuccess(res.message || 'Foto enviada com sucesso.');
          requestEntitiesGetById({
            id: params.id,
          });
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            uploadPhoto: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestDeletePhoto = useCallback(
    (params: DeletePhotoUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        deletePhoto: STATUSPAGE.LOADING,
      }));
      entitiesService
        .deletePhoto(params)
        .then((res) => {
          notifySuccess(res.message || 'Foto deletada com sucesso.');
          requestEntitiesGetById({
            id: params.id,
          });
          handleToggleModalPhoto();
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            deletePhoto: STATUSPAGE.DONE,
          }));
        });
    },
    [handleToggleModalPhoto],
  );

  //Banner
  const requestDeleteBanner = useCallback(
    (params: DeleteBannerUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        deleteBanner: STATUSPAGE.LOADING,
      }));
      entitiesService
        .deleteBanner(params)
        .then((res) => {
          notifySuccess(res.message || 'Banner deletado com sucesso.');
          requestEntitiesGetById({
            id: params.id,
          });
          handleToggleModal();
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            deleteBanner: STATUSPAGE.DONE,
          }));
        });
    },
    [handleToggleModal],
  );

  const requestUploadBanner = useCallback(
    (params: UploadBannerUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        uploadBanner: STATUSPAGE.LOADING,
      }));
      entitiesService
        .uploadBanner(params)
        .then((res) => {
          notifySuccess(res.message || 'Banner enviado com sucesso.');
          requestEntitiesGetById({
            id: params.id,
          });
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            uploadBanner: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  // field assessments

  const requestDeleteFieldAssessments = useCallback(
    (params: DeleteFieldAssessmentsUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        deleteFieldAssessments: STATUSPAGE.LOADING,
      }));
      entitiesService
        .deleteFieldAssessments(params)
        .then((res) => {
          notifySuccess(
            res.message || 'Questão de avaliação excluída com sucesso.',
          );
          requestGetAllFieldAssessments(params);
        })
        .catch((err) => {
          notifyError(
            err.message || 'Erro ao apagar uma questão de avaliação.',
          );
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            deleteFieldAssessments: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestUpdateFieldAssessments = useCallback(
    (params: UpdateFieldAssessmentsUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        updateFieldAssessments: STATUSPAGE.LOADING,
      }));
      entitiesService
        .updateFieldAssessments(params)
        .then((res) => {
          notifySuccess(
            res.message || 'Questão de avaliação atualizada com sucesso.',
          );
          requestGetAllFieldAssessments({
            id: entityId,
          });
          toggleModalFieldAssessments();
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            updateFieldAssessments: STATUSPAGE.DONE,
          }));
        });
    },
    [toggleModalFieldAssessments],
  );

  const requestCreateFieldAssessments = useCallback(
    (params: CreateFieldAssessmentsUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        createFieldAssessments: STATUSPAGE.LOADING,
      }));
      entitiesService
        .createFieldAssessments(params)
        .then((res) => {
          notifySuccess(
            res.message || 'Questão de avaliação criada com sucesso.',
          );
          requestGetAllFieldAssessments({ id: entityId });
          validationFieldAssessments.resetForm();
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            createFieldAssessments: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestGetAllFieldAssessments = useCallback(
    (params: GetAllFieldAssessmentsUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        getAllFieldAssessments: STATUSPAGE.LOADING,
      }));
      entitiesService
        .getAllFieldAssessments(params)
        .then((res) => {
          setListFieldAssessments(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            getAllFieldAssessments: STATUSPAGE.DONE,
          }));
        });
    },
    [],
  );

  const requestGetByIdFieldAssessments = useCallback(
    (params: GetByIdFieldAssessmentsUseCaseInterface.Params) => {
      setLoadingStates((prev) => ({
        ...prev,
        getByIdFieldAssessments: STATUSPAGE.LOADING,
      }));
      entitiesService
        .getByIdFieldAssessments(params)
        .then((res) => {
          setGetByIdResponseFieldAssessments(res);
          toggleModalFieldAssessments();
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => {
          setLoadingStates((prev) => ({
            ...prev,
            getByIdFieldAssessments: STATUSPAGE.DONE,
          }));
        });
    },
    [toggleModalFieldAssessments],
  );

  const validationFieldAssessments = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      nameValueModal: getByIdResponseFieldAssessments?.name || '',
    },
    validationSchema: Yup.object().shape({
      // name: requiredStringSchema,
    }),
    onSubmit: async (values) => {
      if (getByIdResponseFieldAssessments.id) {
        requestUpdateFieldAssessments({
          id: getByIdResponseFieldAssessments.id,
          name: values.nameValueModal,
        });
      } else if (!getByIdResponseFieldAssessments.id) {
        requestCreateFieldAssessments({
          name: values.name,
          organizationId: entityId,
        });
      }
    },
  });

  return {
    //
    requestDeleteFieldAssessments,
    requestCreateFieldAssessments,
    requestGetAllFieldAssessments,
    toggleModalFieldAssessments,
    requestGetByIdFieldAssessments,
    validationFieldAssessments,
    isModalFieldAssessmentsOpen,
    listFieldAssessments,
    getByIdResponseFieldAssessments,
    //
    setDeleteTce,
    handleDeletePhoto,
    handleDeleteBanner,
    handleToggleModal,
    requestDeletePhoto,
    requestUploadPhoto,
    requestUploadBanner,
    requestDeleteBanner,
    requestSearchByCnpj,
    requestEntitiesList,
    requestEntitiesCreate,
    requestEntitiesDelete,
    requestEntitiesUpdate,
    handleToggleModalPhoto,
    requestEntitiesGetById,
    requestGetAddressViaZipCode,
    requestEntitiesSettingUpdate,
    requestEntitiesSettingGetById,
    setCitiesForSelectOptionsData,
    requestGetAllInsuranceCompanies,
    requestGetAllSsoProviders,
    requestGetAllCitiesForSelectOptions,
    setInternshipLocationPhotoImagePreviewSrc,
    deleteTce,
    listStatus,
    validation,
    loadingStates,
    isModalPhotoOpen,
    entitiesListData,
    searchByCnpjData,
    isModalBannerOpen,
    statusEditEntities,
    loadingSearchByCnpj,
    responseEntitiesById,
    statusCreateEntities,
    listInsuranceCompanies,
    listSsoProviders,
    validationFormsSetting,
    loadingEntitiesSetting,
    hasChangedAddressFields,
    citiesForSelectOptionsData,
    responseEntitiesSettingById,
    validationFormRegistrationData,
    getAddressViaZipCodeRequestStatus,
    internshipLocationPhotoImagePreviewSrc,
    statusDeleteEntities,
  };
};
